<template>
  <cv-tile
    kind="clickable"
    class="list-component"
    :class="{ 'has-actions': hasActions }"
    :style="cssVars"
    @click="handleClick"
  >
    <div v-if="hasIcon" class="list-icon">
      <svg-icon :name="icon" kind="indicative" />
    </div>
    <div class="content-wrapper">
      <div v-if="hasTags" class="list-tags">
        <slot name="tags" />
      </div>
      <slot />

      <div class="list-header">
        <h6 class="title">
          <slot name="title" />
        </h6>
        <div class="info">
          <slot name="info" />
          <div class="timestamp">
            <time>
              <slot name="timestamp" />
            </time>
          </div>
        </div>
        <div class="checkbox">
          <slot name="checkbox" />
        </div>
        <slot name="header" />
      </div>

      <slot name="content" />

      <div v-if="hasActions" class="list-actions">
        <slot name="actions" />
      </div>
    </div>
    <svg-icon class="hover-indicator" name="ArrowRight16" kind="action" />
  </cv-tile>
</template>

<script setup lang="ts">
import DOMUtils from "@utils/dom.utils";
import { isEmpty } from "@utils/var.check";
import subjectColorMap from "@configs/subjectColorMap";
import ColorUtils from "@utils/color.utils";
import { computed, onMounted, onBeforeUpdate, useSlots, ref, nextTick } from "vue";

// Props
interface Props {
  icon?: string;
  subjectKey?: string;
}

const { icon = "", subjectKey = "" } = defineProps<Props>();

// Emits
const emit = defineEmits<{
  click: [data: unknown];
}>();

const slots = useSlots();

// Data
const hasActions = ref(!!slots["actions"]);
const hasTags = ref(!!slots["tags"]);

// Computed
const hasIcon = computed(() => {
  return !isEmpty(icon);
});

const cssVars = computed(() => {
  const subjectColor = subjectColorMap.getSubjectColor(subjectKey);

  return {
    "--color": ColorUtils.getTintedColor(subjectColor, 20),
    "--hover-color": ColorUtils.getTintedColor(subjectColor, 30),
    "--active-color": ColorUtils.getTintedColor(subjectColor, 40),
    "--watermark-color": ColorUtils.getTintedColor(subjectColor, 10),
  };
});

// Methods
const handleClick = (event) => {
  if (
    !DOMUtils.parent(event.target, ".list-actions") &&
    !DOMUtils.parent(event.target, ".input-checkbox")
  ) {
    // prevent trigger list component click if overflow button is clicked
    // as both are overlapping
    emit("click", event);
  }
};

const checkSlots = () => {
  nextTick(() => {
    hasActions.value = !!slots["actions"];
    hasTags.value = !!slots["tags"];
  });
};

// Mounted
onMounted(() => {
  checkSlots();
});

// BeforeUpdate
onBeforeUpdate(() => {
  checkSlots();
});
</script>

<style lang="scss">
@use "@styles/abstracts/mixins" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;

.list-component.bx--tile {
  display: flex;
  position: relative;
  padding: $spacing-03;
  border-radius: 10px;
  background-color: $ui-02;
  border: 1px solid transparent;
  word-break: break-word;

  .dashboard-side-section & {
    .list-header {
      .title {
        padding-right: $spacing-05;
      }
    }
  }

  &:not(.empty) {
    &:hover,
    &:active {
      box-shadow: $shadow-01;

      .hover-indicator {
        display: block;
      }
    }

    &:active {
      border-color: $interactive-01;

      .hover-indicator {
        path {
          stroke: $interactive-01;
        }
      }
    }
  }

  &.has-flag-and-overflow-menu {
    .list-header {
      .title {
        margin-right: $layout-04;
      }
    }
  }

  .list-icon {
    display: flex;
    justify-content: center;
    min-width: 40px;

    ~ .content-wrapper {
      max-width: calc(100% - 3rem);
    }
  }

  .content-wrapper {
    width: 100%;
    max-width: 100%;
  }

  .hover-indicator {
    display: none;
    position: absolute;
    right: 1rem;
    bottom: 0.5rem;

    path {
      stroke: $icon-02;
    }
  }

  .list-tags {
    .label-component {
      span {
        padding: $spacing-01 $spacing-03;
      }
    }
  }

  .list-header {
    .title {
      @include carbon--type-style("body-short-01", true);
      padding-right: $spacing-05;
    }

    .info {
      margin-top: $spacing-02;
    }

    .timestamp {
      @include carbon--type-style("card-label-01", true);
      color: $text-02;

      time {
        @include carbon--type-style("card-short-01");
        display: inline-flex;
        align-items: center;

        .sls-icon {
          margin-right: $spacing-02;
        }
      }
    }
  }

  .content-wrapper {
    .mce-content {
      @include carbon--type-style("card-long-01");
      // To cater for space allowance for the hover-indicator
      padding-right: $spacing-05;
    }
  }

  .list-actions {
    display: flex;
    align-items: center;
    position: absolute;
    right: $spacing-03;
    top: $spacing-01;

    > * {
      &:not(:first-child) {
        margin-left: $spacing-03;
      }
    }
  }
}
</style>
